import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { BASE_URL } from '@/constant'

// Unauthenticated API slice for the shareable "no login" leave request page
// (src/pages/PublicLeaveRequest.tsx). Deliberately does NOT attach the auth
// token (unlike baseApi/botApi) — this hits /public/leaves/* on the backend,
// which has no `protect` middleware by design.
export interface PublicEmployee {
  user_id: number
  user_name: string
  department: string | null
  reporting_manager_name?: string | null
}

export interface PublicLeaveBalance {
  id: number
  leave_type: string
  total_assigned: number
  start_date: string
  end_date: string
  status: 'active' | 'inactive'
  consumed: number
  balance: number
}

export interface CreatePublicLeaveRequestPayload {
  employee_id: number
  leave_type: string
  from_date: string
  to_date: string
  from_half?: 'full' | 'first' | 'second'
  to_half?: 'full' | 'first' | 'second'
  reason?: string
  notify?: number[]
}

// Every request below that needs to know "who is this" takes verifyToken
// and sends it as X-Leave-Verify-Token — issued by confirmPublicLeaveOtp,
// required server-side (controller/publicLeave.controller.js#
// requireVerifiedEmployeeId) to derive employee_id instead of trusting
// whatever the request body/query carries.
interface WithVerifyToken {
  verifyToken: string
}

export interface CreatePublicLeaveRequestResponse {
  success: boolean
  message: string
  data: {
    task_id: string
    total_leave_days: number
    approver: {
      clickupId: number | null
      name: string | null
      source: string | null
    }
  }
}

export const publicLeaveApi = createApi({
  reducerPath: 'publicLeaveApi',
  baseQuery: fetchBaseQuery({ baseUrl: `${BASE_URL}/public/leaves` }),
  tagTypes: ['PublicLeaveBalance'],
  endpoints: builder => ({
    getPublicEmployees: builder.query<{ data: PublicEmployee[] }, void>({
      query: () => ({ url: '/employees' }),
    }),
    // The identity gate for the public form: email must match a real
    // employee, then the emailed code must be confirmed
    // (controller/publicLeave.controller.js#sendPublicLeaveOtp /
    // #verifyPublicLeaveOtp). Success returns a verify_token (30 min) that
    // every identity-scoped call below must send back.
    sendPublicLeaveOtp: builder.mutation<
      { success: boolean; message: string },
      { email: string }
    >({
      query: payload => ({
        url: '/verify/send-otp',
        method: 'POST',
        body: payload,
      }),
    }),
    confirmPublicLeaveOtp: builder.mutation<
      { data: PublicEmployee & { verify_token: string } },
      { email: string; otp: string }
    >({
      query: payload => ({
        url: '/verify/confirm-otp',
        method: 'POST',
        body: payload,
      }),
    }),
    getPublicLeaveBalances: builder.query<
      { data: PublicLeaveBalance[] },
      { employee_id: number } & WithVerifyToken
    >({
      query: ({ verifyToken, ...params }) => ({
        url: '/balances',
        params,
        headers: { 'X-Leave-Verify-Token': verifyToken },
      }),
      providesTags: ['PublicLeaveBalance'],
    }),
    createPublicLeaveRequest: builder.mutation<
      CreatePublicLeaveRequestResponse,
      CreatePublicLeaveRequestPayload & WithVerifyToken
    >({
      query: ({ verifyToken, ...payload }) => ({
        url: '/request',
        method: 'POST',
        body: payload,
        headers: { 'X-Leave-Verify-Token': verifyToken },
      }),
      // The balance a leave request consumes only counts once the request
      // is approved (see backend), but re-fetching here still matters: it's
      // what makes a second submission in the same session re-run the
      // overlap guard's effects and pick up anything else that changed,
      // instead of showing the pre-submission balance forever.
      invalidatesTags: ['PublicLeaveBalance'],
    }),
    // Optional follow-up after createPublicLeaveRequest — see the sibling
    // authenticated mutation in redux/service/leaveRequest.ts for why this
    // is a separate call instead of a multipart create request.
    uploadPublicLeaveAttachment: builder.mutation<
      { success: boolean; message: string },
      { taskId: string; file: File }
    >({
      query: ({ taskId, file }) => {
        const formData = new FormData()
        formData.append('attachment', file)
        return {
          url: `/request/${taskId}/attachment`,
          method: 'POST',
          body: formData,
        }
      },
    }),
    emailPublicLeaveReport: builder.mutation<
      { success: boolean; message: string },
      { employee_id: number } & WithVerifyToken
    >({
      query: ({ verifyToken, ...payload }) => ({
        url: '/report/email',
        method: 'POST',
        body: payload,
        headers: { 'X-Leave-Verify-Token': verifyToken },
      }),
    }),
  }),
})

export const {
  useGetPublicEmployeesQuery,
  useSendPublicLeaveOtpMutation,
  useConfirmPublicLeaveOtpMutation,
  useGetPublicLeaveBalancesQuery,
  useCreatePublicLeaveRequestMutation,
  useUploadPublicLeaveAttachmentMutation,
  useEmailPublicLeaveReportMutation,
} = publicLeaveApi
